import { X, Check, Sparkles } from 'lucide-react';
import { useState, useEffect } from 'react';
import { getAvailablePlans } from '../../lib/subscriptions';
import { upgradeSubscription, beginSubscriptionCheckout } from '../../lib/payments';
import type { SubscriptionPlan } from '../../lib/supabase';
import type { SubscriptionWithPlan } from '../../lib/subscriptions';
import { logger } from '../../lib/logger';

interface UpgradePlanModalProps {
  currentSubscription: SubscriptionWithPlan | null;
  userId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  context?: 'default' | 'insufficient_credits';
  requiredCredits?: number;
  availableCredits?: number;
}

export function UpgradePlanModal({
  currentSubscription,
  userId,
  isOpen,
  onClose,
  onSuccess,
  context = 'default',
  requiredCredits = 0,
  availableCredits = 0,
}: UpgradePlanModalProps) {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [processingPlanId, setProcessingPlanId] = useState<string | null>(null);
  const [selectedInterval, setSelectedInterval] = useState<'month' | 'year'>('month');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadPlans();
      // Set default interval based on current subscription
      if (currentSubscription) {
        setSelectedInterval(currentSubscription.billing_cycle === 'annual' ? 'year' : 'month');
      }
    }
  }, [isOpen, currentSubscription]);

  const loadPlans = async () => {
    try {
      const availablePlans = await getAvailablePlans();
      setPlans(availablePlans);
    } catch (err) {
      logger.error('Error loading plans:', err);
      setError('Failed to load plans');
    }
  };

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(0)}`;
  };

  const handleSelectPlan = async (plan: SubscriptionPlan) => {
    // Check if this is the current plan
    if (currentSubscription && currentSubscription.plan_id === plan.id) {
      const currentInterval = currentSubscription.billing_cycle === 'annual' ? 'year' : 'month';
      if (selectedInterval === currentInterval) {
        setError('You are already subscribed to this plan');
        return;
      }
    }

    setProcessingPlanId(plan.id);
    setError(null);

    try {
      const planSlug = plan.slug as 'starter' | 'professional' | 'premium';

      if (currentSubscription) {
        // Upgrade existing subscription (immediate charge + credit carryover)
        const result = await upgradeSubscription(userId, currentSubscription.id, planSlug, selectedInterval);

        if (!result.success) {
          // Handle specific error codes
          if (result.code === 'payment_failed') {
            setError('Payment failed. Your current plan remains active. Please check your payment method.');
          } else {
            setError(result.error || 'Failed to upgrade subscription');
          }
          setProcessingPlanId(null);
          return;
        }

        // Success - credits were carried over
        logger.info(`Upgrade successful. Carried over ${result.carryOverCredits || 0} credits.`);
        onSuccess?.();
        onClose();
      } else {
        // New subscription - redirect to Stripe checkout
        await beginSubscriptionCheckout({
          plan: planSlug,
          interval: selectedInterval,
          userId,
        });
        // Note: beginSubscriptionCheckout redirects, so we don't reach here
      }
    } catch (err: unknown) {
      logger.error('Error selecting plan:', err);
      const message = err instanceof Error ? err.message : 'Failed to process plan selection';
      setError(message);
    } finally {
      setProcessingPlanId(null);
    }
  };

  const isCurrentPlan = (plan: SubscriptionPlan) => {
    if (!currentSubscription) return false;
    return currentSubscription.plan_id === plan.id;
  };

  const getCurrentPlanInterval = () => {
    if (!currentSubscription) return null;
    return currentSubscription.billing_cycle === 'annual' ? 'year' : 'month';
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-charcoal/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-[2rem] shadow-soft max-w-5xl w-full p-8 md:p-10 my-8 relative animate-modal-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Context Banner for Insufficient Credits */}
        {context === 'insufficient_credits' && (
          <div className="bg-terracotta/5 border border-terracotta/20 rounded-2xl p-5 mb-8">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-terracotta/10 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-terracotta" />
              </div>
              <div>
                <h3 className="font-serif text-lg text-charcoal mb-1">Insufficient Credits</h3>
                <p className="text-charcoal/70 font-light text-sm">
                  You need {requiredCredits} credit{requiredCredits !== 1 ? 's' : ''}, but you only have {availableCredits}. 
                  Upgrade your plan to get more credits instantly.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="font-mono text-xs text-sand uppercase tracking-widest mb-2">
              {context === 'insufficient_credits' ? 'Upgrade Required' : 'Subscription Plans'}
            </div>
            <h3 className="font-serif text-3xl text-charcoal tracking-tight">
              {context === 'insufficient_credits' ? 'Get More Credits' : currentSubscription ? 'Change Your Plan' : 'Choose Your Plan'}
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={!!processingPlanId}
            className="w-10 h-10 rounded-full bg-alabaster border border-stone flex items-center justify-center text-sand hover:text-charcoal hover:border-charcoal/30 transition-all duration-300 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-terracotta/5 border border-terracotta/20 rounded-2xl p-4 mb-8">
            <p className="text-terracotta text-sm font-light">{error}</p>
          </div>
        )}

        {/* Billing Interval Toggle */}
        <div className="flex items-center justify-center mb-10">
          <div className="inline-flex bg-alabaster rounded-full border border-stone/50 p-1.5">
            <button
              onClick={() => setSelectedInterval('month')}
              disabled={!!processingPlanId}
              className={`px-6 py-2.5 rounded-full font-light text-sm transition-all duration-300 ${
                selectedInterval === 'month'
                  ? 'bg-olive text-white shadow-soft'
                  : 'text-charcoal hover:bg-stone/30'
              } disabled:opacity-50`}
            >
              Monthly
            </button>
            <button
              onClick={() => setSelectedInterval('year')}
              disabled={!!processingPlanId}
              className={`px-6 py-2.5 rounded-full font-light text-sm transition-all duration-300 relative ${
                selectedInterval === 'year'
                  ? 'bg-olive text-white shadow-soft'
                  : 'text-charcoal hover:bg-stone/30'
              } disabled:opacity-50`}
            >
              Yearly
              <span className="absolute -top-2 -right-2 bg-terracotta text-white text-[10px] font-medium px-2 py-0.5 rounded-full">
                SAVE 17%
              </span>
            </button>
          </div>
        </div>

        {/* Plan Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan, index) => {
            const isCurrent = isCurrentPlan(plan);
            const isCurrentInterval = getCurrentPlanInterval() === selectedInterval;
            const price = selectedInterval === 'year' ? plan.annual_price_cents : plan.monthly_price_cents;
            const features = (plan.features as string[]) || [];
            const isProcessing = processingPlanId === plan.id;
            const isPopular = plan.slug === 'professional';

            return (
              <div
                key={plan.id}
                className={`relative rounded-3xl border transition-all duration-500 p-6 flex flex-col h-full ${
                  isCurrent && isCurrentInterval
                    ? 'bg-olive/5 border-olive ring-2 ring-olive'
                    : isPopular
                    ? 'bg-white border-stone shadow-soft hover:shadow-lift hover:-translate-y-1'
                    : 'bg-white border-stone hover:border-olive/50 hover:-translate-y-0.5'
                }`}
                style={{
                  animation: `drift-in 0.5s ease-out ${index * 0.1}s both`,
                }}
              >
                {/* Current Plan Badge */}
                {isCurrent && isCurrentInterval && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-olive text-white text-xs font-light px-4 py-1 rounded-full shadow-soft">
                    Current Plan
                  </div>
                )}

                {/* Popular Badge */}
                {isPopular && !(isCurrent && isCurrentInterval) && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-terracotta text-white text-xs font-light px-4 py-1 rounded-full shadow-soft">
                    Most Popular
                  </div>
                )}

                {/* Plan Header */}
                <div className="mb-6 pt-2">
                  <h4 className="font-serif text-xl text-charcoal mb-3">{plan.name}</h4>
                  <div className="flex items-baseline gap-1 mb-3">
                    <span className="font-serif text-4xl text-charcoal">
                      {formatPrice(price)}
                    </span>
                    <span className="text-sand font-light text-sm">
                      /{selectedInterval === 'year' ? 'yr' : 'mo'}
                    </span>
                  </div>
                  {plan.description && (
                    <p className="text-sand font-light text-sm leading-relaxed">{plan.description}</p>
                  )}
                </div>

                {/* Credits Highlight */}
                <div className="bg-alabaster rounded-xl p-4 mb-6 border border-stone/30">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-olive/10 flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-olive" />
                    </div>
                    <div>
                      <div className="font-serif text-xl text-charcoal">{plan.credits_per_month}</div>
                      <div className="font-mono text-[10px] text-sand uppercase tracking-widest">credits/month</div>
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="mb-8 flex-grow">
                  <div className="font-mono text-xs text-sand uppercase tracking-widest mb-4">Features</div>
                  <ul className="space-y-3">
                    {features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm text-charcoal/80 font-light">
                        <div className="w-5 h-5 rounded-full bg-olive/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="w-3 h-3 text-olive" />
                        </div>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Action Button */}
                <button
                  onClick={() => handleSelectPlan(plan)}
                  disabled={!!processingPlanId || (isCurrent && isCurrentInterval)}
                  className={`w-full py-4 px-6 rounded-full transition-all duration-500 font-light text-sm tracking-wide ${
                    isCurrent && isCurrentInterval
                      ? 'bg-stone/30 text-sand cursor-not-allowed'
                      : isPopular
                      ? 'bg-olive text-white hover:-translate-y-0.5 hover:shadow-lift'
                      : 'bg-charcoal text-white hover:-translate-y-0.5 hover:shadow-lift'
                  } disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:transform-none`}
                >
                  {isProcessing ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Processing...
                    </span>
                  ) : isCurrent && isCurrentInterval ? (
                    'Current Plan'
                  ) : isCurrent ? (
                    `Switch to ${selectedInterval === 'year' ? 'Yearly' : 'Monthly'}`
                  ) : (
                    'Select Plan'
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center">
          <p className="font-mono text-xs text-sand uppercase tracking-widest">
            All plans include priority support • Cancel anytime
          </p>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes modal-in {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .animate-modal-in {
          animation: modal-in 0.3s ease-out;
        }
        @keyframes drift-in {
          from {
            opacity: 0;
            transform: translateY(12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
